import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import { Users, Plus, Shield, UserCheck } from "lucide-react";

export default function AdminUsersPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [viewAsTarget, setViewAsTarget] = useState(null);
    const [viewAsReason, setViewAsReason] = useState("");
    const [viewAsSubmitting, setViewAsSubmitting] = useState(false);
    const [viewAsError, setViewAsError] = useState(null);
    const { api, swapToken } = useAuth();
    const navigate = useNavigate();

    useEffect(() => { fetchUsers(); }, []);

    const fetchUsers = async () => {
        try {
            const res = await api.get("/v2/admin/users");
            setUsers(res.data.users);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const handleViewAs = async (e) => {
        e.preventDefault();
        if (viewAsReason.trim().length < 20) {
            setViewAsError("Reason must be at least 20 characters.");
            return;
        }
        setViewAsSubmitting(true); setViewAsError(null);
        try {
            const res = await api.post(
                `/v2/admin/impersonations/impersonate/${viewAsTarget.id}`,
                { reason: viewAsReason.trim() }
            );
            const { token, target_user } = res.data;
            await swapToken(token);
            setViewAsTarget(null); setViewAsReason("");
            // Route based on the target's role landing page
            if (target_user.role === "platform_admin") navigate("/admin");
            else if (["enterprise_admin", "super_admin", "enterprise_executive", "business_unit_lead", "business_unit_hrbp", "manager"].includes(target_user.role)) navigate("/enterprise");
            else navigate("/learn");
        } catch (err) {
            setViewAsError(err.response?.data?.detail || "Failed to start session.");
        } finally {
            setViewAsSubmitting(false);
        }
    };

    const [form, setForm] = useState({ email: "", full_name: "", password: "changeme123", role: "employee", enterprise_id: "", persona: "", business_unit: "" });

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form };
            if (!payload.enterprise_id) delete payload.enterprise_id;
            if (!payload.persona) delete payload.persona;
            if (!payload.business_unit) delete payload.business_unit;
            await api.post("/v2/admin/users", payload);
            setShowCreate(false);
            fetchUsers();
        } catch (err) { alert(err.response?.data?.detail || "Failed"); }
    };

    const ROLE_COLORS = {
        platform_admin: "text-white bg-white/10",
        enterprise_executive: "text-blue-400 bg-blue-500/10",
        business_unit_lead: "text-blue-300 bg-blue-500/10",
        manager: "text-gray-300 bg-gray-500/10",
        employee: "text-gray-400 bg-gray-500/10",
        scorer: "text-green-400 bg-green-500/10",
    };

    return (
        <div className="p-8" data-testid="admin-users">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-display font-bold text-white">Users</h1>
                    <p className="text-sm text-gray-500 mt-1">{users.length} accounts across all workspaces</p>
                </div>
                <div className="flex gap-2">
                    <ProvisionOperatorButton api={api} onCreated={fetchUsers} />
                    <button onClick={() => setShowCreate(true)} data-testid="create-user-btn"
                        className="px-4 py-2 bg-white text-black text-sm font-semibold hover:bg-gray-300 transition-colors flex items-center gap-2">
                        <Plus className="w-4 h-4" /> New User
                    </button>
                </div>
            </div>

            <div className="bg-card border border-border/40">
                <div className="grid grid-cols-[2fr_2fr_1fr_1fr_1fr_120px] px-5 py-3 border-b border-border/40 text-xs text-gray-600 font-body">
                    <span>Name</span><span>Email</span><span>Role</span><span>Status</span><span>Last Login</span><span></span>
                </div>
                {users.map((u, i) => (
                    <motion.div key={u.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                        className="grid grid-cols-[2fr_2fr_1fr_1fr_1fr_120px] px-5 py-3 border-b border-border/20 items-center hover:bg-white/[0.01] transition-colors">
                        <span className="text-sm text-white">{u.full_name}</span>
                        <span className="text-sm text-gray-400 font-mono text-xs">{u.email}</span>
                        <span><span className={`text-xs px-2 py-0.5 ${ROLE_COLORS[u.role] || "text-gray-400 bg-gray-500/10"}`}>{u.role?.replace("_", " ")}</span></span>
                        <span className={`text-xs ${u.is_active ? "text-green-400" : "text-red-400"}`}>{u.is_active ? "Active" : "Disabled"}</span>
                        <span className="text-xs text-gray-600 font-mono">{u.last_login_at?.slice(0, 10) || "Never"}</span>
                        <span>
                            {u.role !== "platform_admin" && u.is_active && (
                                <button
                                    onClick={() => { setViewAsTarget(u); setViewAsReason(""); setViewAsError(null); }}
                                    className="flex items-center gap-1.5 px-2.5 py-1 border border-border/40 text-xs text-gray-400 hover:text-white hover:border-white/40 transition-colors"
                                    data-testid={`view-as-btn-${u.id}`}>
                                    <UserCheck className="w-3 h-3" /> View as
                                </button>
                            )}
                        </span>
                    </motion.div>
                ))}
            </div>

            {viewAsTarget && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setViewAsTarget(null)}>
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-card border border-border/40 p-8 w-full max-w-md" onClick={e => e.stopPropagation()}
                        data-testid="view-as-modal">
                        <h2 className="text-lg font-display font-bold text-white mb-2">View as {viewAsTarget.full_name}</h2>
                        <p className="text-xs text-gray-500 mb-1 font-mono">{viewAsTarget.email}</p>
                        <p className="text-xs text-gray-500 mb-5">
                            You will browse the platform as this user for up to 60 minutes. The session is fully audited.
                        </p>
                        <form onSubmit={handleViewAs} className="space-y-4">
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Reason (min 20 chars) *</label>
                                <textarea
                                    value={viewAsReason}
                                    onChange={e => setViewAsReason(e.target.value)}
                                    placeholder="e.g. YC partner demo walkthrough — review Sarah Mensah diagnostic"
                                    rows={3}
                                    className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white outline-none font-body resize-none"
                                    data-testid="view-as-reason"
                                />
                                <p className="text-xs text-gray-600 mt-1 font-mono">{viewAsReason.trim().length} / 20</p>
                            </div>
                            {viewAsError && (
                                <p className="text-xs text-red-300" data-testid="view-as-error">{viewAsError}</p>
                            )}
                            <div className="flex gap-3 pt-2">
                                <button type="submit" disabled={viewAsSubmitting || viewAsReason.trim().length < 20}
                                    className="flex-1 bg-white text-black py-2.5 text-sm font-semibold hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    data-testid="view-as-submit">
                                    {viewAsSubmitting ? "Starting…" : "Start session"}
                                </button>
                                <button type="button" onClick={() => setViewAsTarget(null)}
                                    className="px-4 py-2.5 border border-border/40 text-sm text-gray-400 hover:text-white transition-colors">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            {showCreate && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-card border border-border/40 p-8 w-full max-w-md" onClick={e => e.stopPropagation()}>
                        <h2 className="text-lg font-display font-bold text-white mb-6">Create User</h2>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Full Name *</label>
                                <input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} required
                                    className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white outline-none" />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Email *</label>
                                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required type="email"
                                    className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white outline-none" />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Role *</label>
                                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                                    className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white outline-none">
                                    {["platform_admin","enterprise_executive","business_unit_lead","business_unit_hrbp","manager","employee","support_engineer","scorer"].map(r => <option key={r} value={r}>{r.replace("_"," ")}</option>)}
                                </select>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="submit" className="flex-1 bg-white text-black py-2.5 text-sm font-semibold hover:bg-gray-300 transition-colors">Create</button>
                                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2.5 border border-border/40 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
}


function ProvisionOperatorButton({ api, onCreated }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ full_name: "", email: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const submit = async () => {
        setBusy(true); setFeedback(null);
        try {
            const r = await api.post("/v2/admin/company-operators", form);
            setFeedback({ ok: true, msg: `${r.data.full_name} (${r.data.email}) · temp password: ${r.data.temp_password}` });
            setForm({ full_name: "", email: "", notes: "" });
            onCreated?.();
        } catch (e) {
            setFeedback({ ok: false, msg: e.response?.data?.detail || "Failed" });
        } finally { setBusy(false); }
    };

    return (
        <>
            <button onClick={() => setOpen(true)}
                className="px-4 py-2 border border-border/60 text-white text-sm hover:bg-white/5 transition-colors flex items-center gap-2"
                data-testid="provision-operator-btn">
                <UserCheck className="w-4 h-4" /> Provision Company Operator
            </button>
            {open && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setOpen(false)}>
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-card border border-border/40 p-8 w-full max-w-md" onClick={e => e.stopPropagation()}
                        data-testid="provision-operator-modal">
                        <h2 className="text-xl font-display font-bold text-white mb-4">Provision Company Operator</h2>
                        <p className="text-xs text-gray-500 mb-5 font-body">Creates a new user with role <span className="font-mono text-emerald-300">company_operator</span>. They can create and manage their own companies; they cannot see anyone else's data.</p>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Full name *</label>
                                <input className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                                    value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
                                    data-testid="operator-name-input" />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Email *</label>
                                <input type="email" className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white"
                                    value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                                    data-testid="operator-email-input" />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Notes</label>
                                <textarea className="w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white" rows={2}
                                    value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                            </div>
                        </div>
                        {feedback && (
                            <p className={`text-xs mt-4 font-mono ${feedback.ok ? "text-emerald-300" : "text-red-300"}`}
                                data-testid="operator-feedback">{feedback.msg}</p>
                        )}
                        <div className="flex gap-3 mt-5 pt-4 border-t border-border/30">
                            <button onClick={submit} disabled={busy || !form.full_name || !form.email}
                                className="bg-white text-black px-4 py-2 text-sm font-semibold disabled:opacity-40"
                                data-testid="submit-operator-btn">
                                {busy ? "Provisioning…" : "Provision"}
                            </button>
                            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white text-sm">Close</button>
                        </div>
                    </motion.div>
                </div>
            )}
        </>
    );
}

